import { Button, TOUCH } from "@nymbl/nos2-rules";
import { ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import { toggleAllState } from "@/lib/time/collapsed-projects";
import type { GridGroup } from "@/lib/time/grid";
import { cn } from "@/lib/utils";

interface ToggleAllProjectsProps {
  /** The project headings on screen. */
  groups: GridGroup[];
  collapsed: ReadonlySet<string>;
  /** Values that did not save: their project stays open so the prompt is never hidden. */
  pending: Record<string, unknown>;
  onSetAll: (shown: string[], open: boolean) => void;
  className?: string;
}

/** Collapse all while any project is open, else Expand all. Needs two projects to mean anything. */
export function ToggleAllProjects({
  groups,
  collapsed,
  pending,
  onSetAll,
  className,
}: ToggleAllProjectsProps) {
  if (groups.length < 2) return null;
  const shown = groups.map((g) => g.key);
  const held = groups
    .filter((g) => g.rows.some((r) => r.cells.some((c) => pending[c.key])))
    .map((g) => g.key);
  const { anyOpen, stuckOpen } = toggleAllState(shown, collapsed, held);
  const Icon = anyOpen ? ChevronsDownUp : ChevronsUpDown;
  return (
    <Button
      type="button"
      variant="ghost"
      size="sm"
      className={cn(TOUCH, "shrink-0 font-semibold", className)}
      aria-expanded={anyOpen}
      disabled={stuckOpen}
      title={stuckOpen ? "A project has a value that did not save" : undefined}
      onClick={() => onSetAll(shown, !anyOpen)}
    >
      <Icon className="size-4" aria-hidden="true" />
      {anyOpen ? "Collapse all" : "Expand all"}
    </Button>
  );
}
